import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';

export interface MergeTagDefinition {
  tag: string;
  label: string;
  description: string;
}

export interface SystemEmailAttachment {
  fileName: string;
  gcsPath: string;
  contentType: string;
  sizeBytes?: number;
}

export interface SystemEmailTemplate {
  id: string | null;
  emailType: string;
  subjectLine: string;
  bodyHtml: string;
  bodyText: string;
  isCustom: boolean;
  attachments: SystemEmailAttachment[];
  mergeTags: MergeTagDefinition[];
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface SystemEmailPreview {
  subject: string;
  html: string;
  text: string;
}

export const getSystemEmailTemplateAPI = async (emailType: string) => {
  const response = await baseAPI.get<SystemEmailTemplate>(
    `/email/templates/${emailType}`,
  );
  return response.data;
};

export const useGetSystemEmailTemplate = (
  emailType: string,
  enabled = true,
) => {
  return useQuery({
    queryKey: [KEYS.SYSTEM_EMAIL_TEMPLATE, emailType],
    queryFn: () => getSystemEmailTemplateAPI(emailType),
    enabled: enabled && !!emailType,
  });
};

export const upsertSystemEmailTemplateAPI = async (params: {
  emailType: string;
  subjectLine: string;
  bodyHtml: string;
  keepAttachmentPaths?: string[];
  attachments?: File[];
}) => {
  const formData = new FormData();
  formData.append('subjectLine', params.subjectLine);
  formData.append('bodyHtml', params.bodyHtml);
  if (params.keepAttachmentPaths?.length) {
    formData.append(
      'keepAttachmentPaths',
      params.keepAttachmentPaths.join(','),
    );
  }
  for (const file of params.attachments ?? []) {
    formData.append('attachments', file);
  }

  // Do not set Content-Type: the client must attach multipart boundary.
  const response = await baseAPI.put<SystemEmailTemplate>(
    `/email/templates/${params.emailType}`,
    formData,
  );
  return response.data;
};

export const useUpsertSystemEmailTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: upsertSystemEmailTemplateAPI,
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        [KEYS.SYSTEM_EMAIL_TEMPLATE, variables.emailType],
        data,
      );
      void queryClient.invalidateQueries({
        queryKey: [KEYS.SYSTEM_EMAIL_TEMPLATE, variables.emailType],
      });
      void queryClient.invalidateQueries({ queryKey: [KEYS.EMAIL_TOGGLES] });
    },
  });
};

export const resetSystemEmailTemplateAPI = async (emailType: string) => {
  const response = await baseAPI.delete<{ success: true }>(
    `/email/templates/${emailType}`,
  );
  return response.data;
};

export const useResetSystemEmailTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: resetSystemEmailTemplateAPI,
    onSuccess: (_data, emailType) => {
      void queryClient.invalidateQueries({
        queryKey: [KEYS.SYSTEM_EMAIL_TEMPLATE, emailType],
      });
      void queryClient.invalidateQueries({ queryKey: [KEYS.EMAIL_TOGGLES] });
    },
  });
};

export const previewSystemEmailTemplateAPI = async (params: {
  emailType: string;
  subjectLine: string;
  bodyHtml: string;
}) => {
  const response = await baseAPI.post<SystemEmailPreview>(
    `/email/templates/${params.emailType}/preview`,
    {
      subjectLine: params.subjectLine,
      bodyHtml: params.bodyHtml,
    },
  );
  return response.data;
};

export const usePreviewSystemEmailTemplate = () => {
  return useMutation({
    mutationFn: previewSystemEmailTemplateAPI,
  });
};
